import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Box, Tooltip, useTheme } from '@mui/material'

export type TextTabItem<T extends string> = {
  id: T
  label: string
  /** Shown when `showLabels` is false (narrow layout). Falls back to `label`. */
  shortLabel?: string
  /** Optional compact icon shown instead of `shortLabel` in narrow layouts. */
  shortIcon?: ReactNode
  /** Tints the tab label and its active indicator (e.g. to flag an over-utilized element). */
  color?: string
  /** Fills the whole tab with this colour (e.g. the orange of an over-utilized η). */
  background?: string
}

export type TextTabsProps<T extends string> = {
  value: T
  onChange: (next: T) => void
  items: TextTabItem<T>[]
  showLabels?: boolean
  /** When true, tabs keep their natural width and the row scrolls horizontally (with edge chevron
   *  buttons) instead of being squeezed to fit. `showLabels` still controls long vs. short labels. */
  scrollable?: boolean
}

const TAB_HEIGHT = 36

export const TextTabs = <T extends string>({
  value,
  onChange,
  items,
  showLabels = false,
  scrollable = false,
}: TextTabsProps<T>): ReactElement => {
  const theme = useTheme()

  // Only scroll while full labels are shown; with short labels the tabs fit and stay evenly spaced.
  const isScrollable = scrollable && showLabels

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [overflow, setOverflow] = useState({ left: false, right: false })

  const updateOverflow = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    const left = scrollLeft > 1
    const right = scrollLeft + clientWidth < scrollWidth - 1
    setOverflow(prev => (prev.left === left && prev.right === right ? prev : { left, right }))
  }, [])

  // Track horizontal overflow so the edge chevrons only appear when there's hidden content.
  useEffect(() => {
    if (!isScrollable) return
    const el = scrollRef.current
    if (!el) return
    updateOverflow()
    const observer = new ResizeObserver(updateOverflow)
    observer.observe(el)
    el.addEventListener('scroll', updateOverflow, { passive: true })
    return () => {
      observer.disconnect()
      el.removeEventListener('scroll', updateOverflow)
    }
  }, [isScrollable, items, updateOverflow])

  // Keep the active tab in view when the selection changes (e.g. following a scene click).
  useLayoutEffect(() => {
    if (!isScrollable) return
    const activeEl = scrollRef.current?.querySelector<HTMLElement>('[data-active="true"]')
    activeEl?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [isScrollable, value])

  const scrollByStep = (direction: -1 | 1) => {
    const el = scrollRef.current
    if (!el) return
    // Page by most of the visible width so a couple of clicks reach either end.
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' })
  }

  const renderTab = (item: TextTabItem<T>): ReactElement => {
    const active = value === item.id
    const showLabel = showLabels
    const compactContent = item.shortIcon ?? item.shortLabel ?? item.label
    const activeColor = item.color ?? theme.palette.primary.main
    const inactiveColor = item.color ?? theme.palette.text.secondary
    return (
      <Tooltip key={item.id} title={showLabel ? '' : item.label} arrow disableInteractive>
        <Box
          component="button"
          type="button"
          data-active={active}
          onClick={() => onChange(item.id)}
          sx={{
            ...(isScrollable
              ? { flex: '0 0 auto', px: 1.5, whiteSpace: 'nowrap' }
              : { flex: 1, minWidth: 0 }),
            height: TAB_HEIGHT,
            cursor: 'pointer',
            background: item.background ?? 'transparent',
            border: 0,
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Roboto',
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1,
            color: active ? activeColor : inactiveColor,
            transition: 'background-color 120ms',
            // Layer the hover tint over the fill so tinted tabs keep their colour on hover.
            '&:hover': {
              backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 2,
              background: active ? activeColor : 'transparent',
            },
          }}
        >
          {showLabel ? item.label : compactContent}
        </Box>
      </Tooltip>
    )
  }

  const renderScrollButton = (direction: -1 | 1): ReactElement => (
    <Box
      component="button"
      type="button"
      aria-label={direction === -1 ? 'Tabs nach links scrollen' : 'Tabs nach rechts scrollen'}
      onClick={() => scrollByStep(direction)}
      sx={{
        // Overlay the edge rather than taking layout width — otherwise the chevrons appearing /
        // disappearing would change the scroll container's width mid-scroll and the ends would be
        // unreachable in one click.
        position: 'absolute',
        top: 0,
        bottom: 0,
        ...(direction === -1 ? { left: 0 } : { right: 0 }),
        zIndex: 1,
        width: 24,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        border: 0,
        background: theme.palette.common.white,
        color: theme.palette.text.secondary,
        // Opaque hover (not the translucent action.hover) so the overlaid tab text can't shine
        // through this button.
        '&:hover': { background: theme.palette.grey[100], color: theme.palette.primary.main },
      }}
    >
      {direction === -1 ? (
        <ChevronLeft sx={{ fontSize: 18 }} />
      ) : (
        <ChevronRight sx={{ fontSize: 18 }} />
      )}
    </Box>
  )

  const rowSx = {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'stretch',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
  } as const

  if (!isScrollable) return <Box sx={rowSx}>{items.map(renderTab)}</Box>

  return (
    <Box sx={{ ...rowSx, position: 'relative' }}>
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {items.map(renderTab)}
      </Box>
      {overflow.left && renderScrollButton(-1)}
      {overflow.right && renderScrollButton(1)}
    </Box>
  )
}
